import { useState, useMemo, useId } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/utils/cn";
import type { DraftCalendarItem, DraftCalendarSortField, DraftCalendarSortDir, DraftCalendarTableProps } from "@/types/draftCalendar";
import { getSummitMeetingUrl } from "@/config/env";
import { InfoTooltip } from "@/features/dashboard/components/InfoTooltip";
import mablegridImg from "@/imgs/mablegrid.png";
import { Statuses } from "@/utils/statuses";
import "./DraftCalendarTable.css";

// ─── Sort logic ───────────────────────────────────────────────────────────────

type SortField = DraftCalendarSortField;
type SortDir = DraftCalendarSortDir;

function getSortValue(item: DraftCalendarItem, field: SortField): string | number {
  switch (field) {
    case "meetingId":
      if (item.meetingId) return `0_${item.meetingId}`;
      if (item.summitIntegrationStatus === "VALIDATION_ERROR" || item.summitIntegrationStatus === "SERVER_ERROR")
        return "1_error";
      if (item.summitIntegrationStatus === "PUSHED_TO_SUMMIT") return "2_pushed";
      return "3_none";
    case "summitMeetingStatus":
      return item.summitMeetingStatusSortOrder;
    case "duMeetingStatus":
      return item.duMeetingStatusSortOrder;
    case "isRead":
      return item.isRead ? 1 : 0;
    case "unSeenComments":
      return item.unSeenComments ? 1 : 0;
    case "showInGRC":
      return item.showInGRC ? 1 : 0;
    case "nightGuestNumber":
    case "allInAttendees":
      return item[field] ?? 0;
    case "eventStart":
    case "eventEnd":
    case "roomBlockStart":
    case "roomBlockEnd":
      return item[field] ?? "";
    default:
      return (item[field] as string | null) ?? "";
  }
}

function sortItems(items: DraftCalendarItem[], field: SortField, dir: SortDir) {
  return [...items].sort((a, b) => {
    const va = getSortValue(a, field);
    const vb = getSortValue(b, field);
    const cmp = typeof va === "number" && typeof vb === "number"
      ? va - vb
      : String(va).localeCompare(String(vb));
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

// DraftCalendarTableProps is defined in @/types/draftCalendar

function formatDate(d: string | null): string {
  if (!d) return "-";
  try {
    const date = new Date(d);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${days[date.getDay()]}, ${mm}-${dd}-${date.getFullYear()}`;
  } catch { return d; }
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
  return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

// ─── Column definitions ───────────────────────────────────────────────────────

interface ColumnDef {
  key: SortField;
  label: string;
  className?: string;
  style?: React.CSSProperties;
  sortable?: boolean;
  visibility?: "always" | "admin-scheduler" | "toggle-settings" | "sign-off-visible";
  /** Optional custom header node — used for icon-only headers (e.g. mablegrid image) */
  renderHeader?: () => React.ReactNode;
}

const COLUMNS: ColumnDef[] = [
  // ── Fixed icon columns ───────────────────────────────────────────────────
  { key: "isRead", label: "Read", className: "w-8", sortable: false, visibility: "always",
    renderHeader: () => (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM14.17 12.76L13.07 11.66C13.78 10.33 13.6 8.65 12.48 7.53C11.79 6.84 10.9 6.5 10 6.5C9.97 6.5 9.94 6.51 9.91 6.51L11 7.6L9.94 8.66L7.11 5.83L9.94 3L11 4.06L10.04 5.02C11.31 5.03 12.57 5.5 13.54 6.46C15.24 8.17 15.45 10.82 14.17 12.76ZM12.89 14.17L10.06 17L9 15.94L9.95 14.99C8.69 14.98 7.43 14.49 6.47 13.53C4.76 11.82 4.55 9.18 5.83 7.24L6.93 8.34C6.22 9.67 6.4 11.35 7.52 12.47C8.22 13.17 9.15 13.51 10.08 13.48L9 12.4L10.06 11.34L12.89 14.17Z" fill="black" />
      </svg>
    )
  },
  { key: "unSeenComments", label: "Comments", className: "w-12", sortable: false, visibility: "always",
    renderHeader: () => (
      <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.2413 2.5C18.2413 1.5375 17.4625 0.75 16.5 0.75H2.5C1.5375 0.75 0.75 1.5375 0.75 2.5V13C0.75 13.9625 1.5375 14.75 2.5 14.75H14.75L18.25 18.25L18.2413 2.5ZM16.5 2.5V14.0238L15.4763 13H2.5V2.5H16.5ZM4.25 9.5H14.75V11.25H4.25V9.5ZM4.25 6.875H14.75V8.625H4.25V6.875ZM4.25 4.25H14.75V6H4.25V4.25Z" fill="gray" />
      </svg>
    )
  },
  { key: "showInGRC", label: "GRC", className: "w-8", sortable: false, visibility: "toggle-settings",
    renderHeader: () => (
      <div className="draft-toggle">
        <img src={mablegridImg} alt="" style={{ height: 15 }} />
      </div>
    )
  },
  // ── Status columns ───────────────────────────────────────────────────────
  { key: "signOffStatus",       label: "Sign Off Status",       className: "min-w-[140px]", sortable: false, visibility: "always" },
  { key: "state",               label: "Draft Meeting Status",          className: "w-32",          sortable: false, visibility: "always" },
  // Summit / DU statuses — sortable via custom sort-order fields (mirrors Blazor)
  { key: "summitMeetingStatus", label: "Summit Meeting Status", className: "min-w-[180px]", sortable: true,  visibility: "toggle-settings" },
  { key: "duMeetingStatus",     label: "DU Status",             className: "min-w-[120px]", sortable: true,  visibility: "toggle-settings" },
  // ── Toggle-settings columns ──────────────────────────────────────────────
  { key: "status",              label: "Tier",                  className: "w-16",          sortable: false, visibility: "toggle-settings" },
  { key: "priorId",             label: "Prior ID",              className: "w-20",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "toggle-settings" },
  { key: "meetingId",           label: "New ID",                className: "w-20",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "always" },
  // ── Core meeting columns ─────────────────────────────────────────────────
  { key: "business",            label: "Business",              className: "min-w-[120px]", sortable: false, visibility: "always" },
  { key: "meetingName",         label: "Meeting Name",          className: "min-w-[200px]", sortable: false, visibility: "always" },
  { key: "roomBlockStart",      label: "Room Block Start",      className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "always" },
  { key: "roomBlockEnd",        label: "Room Block End",        className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "always" },
  { key: "eventStart",          label: "Meeting Start",         className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "always" },
  { key: "eventEnd",            label: "Meeting End",           className: "w-30",          style: { width: "max-content", whiteSpace: "nowrap" }, sortable: false, visibility: "always" },
  { key: "startTimeString",     label: "Start Time",            className: "w-24",          sortable: false, visibility: "always" },
  { key: "endTimeString",       label: "End Time",              className: "w-24",          sortable: false, visibility: "always" },
  { key: "nightGuestNumber",    label: "Night Guests",          className: "w-20",          sortable: false, visibility: "always",
    renderHeader: () => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.375 9.9325V7.5C18.375 6.05625 17.1938 4.875 15.75 4.875H12.25C11.5763 4.875 10.9637 5.1375 10.5 5.5575C10.0363 5.1375 9.42375 4.875 8.75 4.875H5.25C3.80625 4.875 2.625 6.05625 2.625 7.5V9.9325C2.09125 10.4137 1.75 11.105 1.75 11.875V17.125H3.5V15.375H17.5V17.125H19.25V11.875C19.25 11.105 18.9088 10.4137 18.375 9.9325ZM12.25 6.625H15.75C16.2312 6.625 16.625 7.01875 16.625 7.5V9.25H11.375V7.5C11.375 7.01875 11.7688 6.625 12.25 6.625ZM4.375 7.5C4.375 7.01875 4.76875 6.625 5.25 6.625H8.75C9.23125 6.625 9.625 7.01875 9.625 7.5V9.25H4.375V7.5ZM3.5 13.625V11.875C3.5 11.3938 3.89375 11 4.375 11H16.625C17.1062 11 17.5 11.3938 17.5 11.875V13.625H3.5Z" fill="black" />
        </svg>
        {/* Mirrors Blazor #nightguest-info-icon TelerikTooltip */}
        <InfoTooltip text="Total overnight guests for all event nights combined." placement="bottom" />
      </div>
    )
  },
  { key: "allInAttendees",           label: "All In Attendees",      className: "w-24",          sortable: false, visibility: "always" },
  { key: "meetingType",              label: "Meeting Type",          className: "min-w-[130px]", sortable: false, visibility: "always" },
  { key: "businessLine",             label: "Business Line",         className: "min-w-[140px]", sortable: false, visibility: "always" },
  { key: "attendeeLevel",            label: "Attendee Level",        className: "min-w-[140px]", sortable: false, visibility: "always" },
  { key: "meetingCategory",          label: "Meeting Category",      className: "min-w-[140px]", sortable: false, visibility: "always" },
  { key: "summitProgramOwnerName",   label: "CLO or Sponsoring PPMD",className: "min-w-[200px]", sortable: false, visibility: "always" },
  { key: "summitProgramContact",     label: "Program Contact",       className: "min-w-[150px]", sortable: false, visibility: "always" },
  { key: "summitMeetingPlanner",     label: "Business Advisor",      className: "min-w-[150px]", sortable: false, visibility: "always" },
  { key: "programManager",           label: "Program Manager",       className: "min-w-[150px]", sortable: false, visibility: "always" },
];

/**
 * Sortable, selectable Draft Calendar list table with full column parity to Blazor.
 */
export function DraftCalendarTable({
  items,
  searchTerm,
  selected,
  onSelectionChange,
  onRowClick,
  canEdit,
  canBulkAction,
  canToggleSettings = false,
  signOffProcessStarted = false,
  isAdmin = false,
  isScheduler = false,
  onToggleGRC
}: DraftCalendarTableProps) {
  const [sortField, setSortField] = useState<SortField>("eventStart");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const selectAllId = useId();

  // Filter columns based on visibility rules
  const visibleColumns = useMemo(() => {
    return COLUMNS.filter((col) => {
      switch (col.visibility) {
        case "toggle-settings":
          return canToggleSettings;
        case "sign-off-visible":
          return signOffProcessStarted || isAdmin || isScheduler;
        case "admin-scheduler":
          return isAdmin || isScheduler;
        case "always":
        default:
          return true;
      }
    });
  }, [canToggleSettings, signOffProcessStarted, isAdmin, isScheduler]);

  // Client-side filter by meeting name / business / ID
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? items.filter(
          (i) =>
            i.meetingName?.toLowerCase().includes(q) ||
            i.business?.toLowerCase().includes(q) ||
            i.meetingId?.toLowerCase().includes(q)
        )
      : items;
  }, [items, searchTerm]);

  const sorted = useMemo(
    () => sortItems(filtered, sortField, sortDir),
    [filtered, sortField, sortDir]
  );

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function toggleAll() {
    if (selected.size === sorted.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(sorted.map((i) => i.id)));
    }
  }

  function toggleOne(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  const allSelected = sorted.length > 0 && selected.size === sorted.length;
  const someSelected = selected.size > 0 && selected.size < sorted.length;

  return (
    <div className="draft-calendar-table-scroll">
      <table className="w-full border-collapse text-sm" aria-label="Draft calendar meetings">
        <thead className="sticky top-0 z-10 bg-[#E8E8E8]">
          <tr className="border-b border-slate-200">
            {/* Checkbox column */}
            {canBulkAction && (
              <th scope="col" className="w-10 px-3 py-2">
                <input
                  id={selectAllId}
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </th>
            )}

            {visibleColumns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn("px-3 py-2 text-left text-xs font-semibold text-black", col.className)}
                style={col.style}
                aria-sort={
                  sortField === col.key
                    ? sortDir === "asc" ? "ascending" : "descending"
                    : "none"
                }
              >
                {col.sortable !== false ? (
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className="inline-flex items-center gap-1 hover:text-slate-900"
                  >
                    {col.renderHeader ? col.renderHeader() : col.label}
                    <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    {col.renderHeader ? col.renderHeader() : col.label}
                  </span>
                )}
              </th>
            ))}

          </tr>
        </thead>

        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td
                colSpan={canBulkAction ? visibleColumns.length + 1 : visibleColumns.length}
                className="py-10 text-center text-sm text-slate-500"
              >
                No meetings match your search.
              </td>
            </tr>
          ) : (
            sorted.map((item, idx) => (
              <tr
                key={item.id}
                className={cn(
                  "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                  item.state?.toLowerCase() === "removed" ? "removed-meeting-row" : idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]",
                  selected.has(item.id) && "ring-1 ring-inset ring-slate-400"
                )}
                onDoubleClick={() => onRowClick(item)}
              >
                {canBulkAction && (
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.meetingName ?? item.id}`}
                      checked={selected.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </td>
                )}

                {/* Render cells for visible columns */}
                {visibleColumns.map((col) => (
                  <td
                    key={`${item.id}-${col.key}`}
                    className={cn("px-3 py-2 text-slate-700", col.className)}
                    style={col.style}
                    onClick={(e) => col.key === "showInGRC" && e.stopPropagation()}
                  >
                    <CellRenderer
                      item={item}
                      field={col.key}
                      canToggleSettings={canToggleSettings}
                      onToggleGRC={onToggleGRC}
                    />
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Cell Renderer ──────────────────────────────────────────────────────────────

interface CellRendererProps {
  item: DraftCalendarItem;
  field: SortField;
  canToggleSettings: boolean;
  onToggleGRC?: (id: number, showInGRC: boolean) => void;
}

function CellRenderer({ item, field, canToggleSettings, onToggleGRC }: CellRendererProps) {
  switch (field) {
    case "isRead":
      return item.isRead ? (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" y="0.5" width="10" height="10" rx="5" fill="#BBBCBC" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" y="0.5" width="10" height="10" rx="5" fill="#007CB0" />
        </svg>
      );

    case "unSeenComments":
      if (item.hasComments && item.unSeenComments) {
        return (
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.2413 3.5C19.2413 2.5375 18.4625 1.75 17.5 1.75H3.5C2.5375 1.75 1.75 2.5375 1.75 3.5V14C1.75 14.9625 2.5375 15.75 3.5 15.75H15.75L19.25 19.25L19.2413 3.5ZM15.75 12.25H5.25V10.5H15.75V12.25ZM15.75 9.625H5.25V7.875H15.75V9.625ZM15.75 7H5.25V5.25H15.75V7Z" fill="black" />
            <circle cx="17" cy="5" r="4" fill="#C20000" />
          </svg>
        );
      }
      if (item.hasComments) {
        return (
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.2413 3.5C19.2413 2.5375 18.4625 1.75 17.5 1.75H3.5C2.5375 1.75 1.75 2.5375 1.75 3.5V14C1.75 14.9625 2.5375 15.75 3.5 15.75H15.75L19.25 19.25L19.2413 3.5ZM15.75 12.25H5.25V10.5H15.75V12.25ZM15.75 9.625H5.25V7.875H15.75V9.625ZM15.75 7H5.25V5.25H15.75V7Z" fill="black" />
          </svg>
        );
      }
      return (
        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.2413 2.5C18.2413 1.5375 17.4625 0.75 16.5 0.75H2.5C1.5375 0.75 0.75 1.5375 0.75 2.5V13C0.75 13.9625 1.5375 14.75 2.5 14.75H14.75L18.25 18.25L18.2413 2.5ZM16.5 2.5V14.0238L15.4763 13H2.5V2.5H16.5ZM4.25 9.5H14.75V11.25H4.25V9.5ZM4.25 6.875H14.75V8.625H4.25V6.875ZM4.25 4.25H14.75V6H4.25V4.25Z" fill="gray" />
        </svg>
      );

    case "showInGRC": {
      if (!canToggleSettings) return null;
      // Mirrors Blazor: disabled when State == Removed || State == DUDeclined
      const isStateDisabled = item.state === "Removed" || item.state === "DU Declined";
      const toggleTitle = isStateDisabled
        ? "Cannot update GRC: meeting is in Removed or DU Declined state"
        : item.showInGRC ? "In GRC \u2013 click to remove" : "Not in GRC \u2013 click to add";
      return (
        <div className="center-checkbox" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            role="switch"
            aria-checked={item.showInGRC}
            aria-label={item.showInGRC ? "In GRC – click to remove" : "Not in GRC – click to add"}
            title={toggleTitle}
            disabled={isStateDisabled}
            onClick={() => {
              if (!isStateDisabled) onToggleGRC?.(item.id, !item.showInGRC);
            }}
            className="relative h-5 w-9 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
            style={item.showInGRC ? { background: "#26890D" } : { background: "#111" }}
          >
            <span
              className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
              style={{ left: item.showInGRC ? "18px" : "2px" }}
            />
          </button>
        </div>
      );
    }

    case "signOffStatus":
      return <div className="text-xs" title={item.signOffStatus ?? ""}>{item.signOffStatus ?? "—"}</div>;

    case "state":
      return <StateBadge state={item.state ?? ""} />;

    case "status": {
      const tier = (item.status ?? "").toLowerCase();
      if (!tier) return null;
      const cssClass = tier === "core" ? "event-status-draft-Core" : tier === "secondary" ? "event-status-draft-Secondary" : "";
      return (
        <div style={{ display: "grid", justifyContent: "center" }}>
          <span className={cssClass} title={item.status ?? ""} style={{ flexShrink: 0 }} />
        </div>
      );
    }

    case "priorId":
      return <span className="text-xs">{item.priorId ?? "—"}</span>;

    case "meetingId": {
      const meetingId = item.meetingId;
      const hasMeetingId = !!meetingId;
      const showPushed = !hasMeetingId && item.state === Statuses.Resolved;
      const showError = !hasMeetingId && (item.summitIntegrationStatus === "VALIDATION_ERROR" || item.state === "SERVER_ERROR");
      const summitUrl = hasMeetingId ? getSummitMeetingUrl(meetingId) : "";
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%" }}>
          {hasMeetingId && summitUrl ? (
            <a href={summitUrl} target="_blank" rel="noopener noreferrer"
              className="meeting-id-link-cell" onClick={(e) => e.stopPropagation()}>
              {meetingId}
            </a>
          ) : hasMeetingId ? (
            <span className="text-xs font-mono" style={{ color: "#007CB0" }}>{meetingId}</span>
          ) : null}
          {!hasMeetingId && showError && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.9925 1.5C4.8525 1.5 1.5 4.86 1.5 9C1.5 13.14 4.8525 16.5 8.9925 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 8.9925 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15ZM9.375 5.25H8.25V9.75L12.1875 12.1125L12.75 11.19L9.375 9.1875V5.25Z" fill="#CC1616" />
            </svg>
          )}
          {!hasMeetingId && !showError && showPushed && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.9925 1.5C4.8525 1.5 1.5 4.86 1.5 9C1.5 13.14 4.8525 16.5 8.9925 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 8.9925 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15ZM9.375 5.25H8.25V9.75L12.1875 12.1125L12.75 11.19L9.375 9.1875V5.25Z" fill="#838383" />
            </svg>
          )}
        </div>
      );
    }

    case "business":
      return <span title={item.business ?? ""}>{item.business ?? "—"}</span>;

    case "meetingName":
      return (
        <span className="block truncate font-medium text-slate-900" title={item.meetingName ?? ""}>
          {item.meetingName ?? "—"}
        </span>
      );

    case "roomBlockStart":
      return <span className="text-xs">{formatDate(item.roomBlockStart)}</span>;
    case "roomBlockEnd":
      return <span className="text-xs">{formatDate(item.roomBlockEnd)}</span>;
    case "eventStart":
      return <span className="text-xs">{formatDate(item.eventStart)}</span>;
    case "eventEnd":
      return <span className="text-xs">{formatDate(item.eventEnd)}</span>;
    case "startTimeString":
      return <span className="text-xs">{item.startTimeString || "—"}</span>;
    case "endTimeString":
      return <span className="text-xs">{item.endTimeString || "—"}</span>;

    case "nightGuestNumber": {
      // Mirrors Blazor tooltip-target-nightguest: split NightGuestTooltip by ';' and show each line
      const tooltipLines = item.nightGuestTooltip
        ? item.nightGuestTooltip.split(";").map((s) => s.trim()).filter(Boolean)
        : [];
      const tooltipText =
        tooltipLines.length > 0
          ? tooltipLines.join("\n")
          : `Night guests: ${item.nightGuestNumber ?? 0}`;
      return (
        <span className="inline-flex items-center gap-1 text-xs">
          {item.nightGuestNumber ?? "\u2014"}
          <InfoTooltip text={tooltipText} contentClassName="nightguest-tooltip-content" />
        </span>
      );
    }

    case "allInAttendees":
      return <span className="text-xs">{item.allInAttendees ?? "—"}</span>;
    case "meetingType":
      return <span className="text-xs" title={item.meetingType ?? ""}>{item.meetingType ?? "—"}</span>;
    case "businessLine":
      return <span className="text-xs" title={item.businessLine ?? ""}>{item.businessLine ?? "—"}</span>;
    case "attendeeLevel":
      return <span className="text-xs" title={item.attendeeLevel ?? ""}>{item.attendeeLevel ?? "—"}</span>;
    case "meetingCategory":
      return <span className="text-xs" title={item.meetingCategory ?? ""}>{item.meetingCategory ?? "—"}</span>;
    case "summitProgramOwnerName":
      return <span className="text-xs" title={item.summitProgramOwnerName ?? ""}>{item.summitProgramOwnerName ?? "—"}</span>;
    case "summitProgramContact":
      return <span className="text-xs" title={item.summitProgramContact ?? ""}>{item.summitProgramContact ?? "—"}</span>;
    case "summitMeetingPlanner":
      return <span className="text-xs" title={item.summitMeetingPlanner ?? ""}>{item.summitMeetingPlanner ?? "—"}</span>;
    case "programManager":
      return <span className="text-xs" title={item.programManager ?? ""}>{item.programManager ?? "—"}</span>;
    case "summitMeetingStatus":
      return <span className="text-xs">{item.summitMeetingStatus ?? "—"}</span>;
    case "duMeetingStatus":
      return <span className="text-xs">{item.duMeetingStatus ?? "—"}</span>;

    default:
      return null;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const STATE_COLORS: Record<string, string> = {
  [Statuses.Draft]:             "bg-slate-100 text-slate-700",
  [Statuses.NewRequest]:        "bg-slate-100 text-slate-700",
  [Statuses.BusinessReview]:    "bg-blue-50 text-blue-700",
  [Statuses.BusinessApproved]:  "bg-blue-100 text-blue-700",
  [Statuses.DUReview]:          "bg-purple-100 text-purple-700",
  [Statuses.Scheduled]:         "bg-cyan-100 text-cyan-800",
  [Statuses.Resolved]:          "bg-green-100 text-green-800",
  [Statuses.CreatedInSummit]:   "bg-green-100 text-green-800",
  [Statuses.ConfirmedEvent]:    "bg-green-100 text-green-800",
  [Statuses.DUDeclined]:        "bg-orange-100 text-orange-700",
  [Statuses.Removed]:           "bg-red-100 text-red-700",
  [Statuses.Softhold]:          "bg-yellow-100 text-yellow-800",
  [Statuses.DidNotMaterialize]: "bg-slate-100 text-slate-500",
  [Statuses.CNFCREATEDSCS]:     "bg-slate-100 text-slate-600",
  // Legacy / extra values seen in existing data
  Submitted:  "bg-blue-100 text-blue-700",
  Confirmed:  "bg-green-100 text-green-800",
  Cancelled:  "bg-red-100 text-red-700",
  Declined:   "bg-orange-100 text-orange-700",
};

function StateBadge({ state }: { state: string }) {
  return (
    <span className="text-xs font-medium whitespace-nowrap text-slate-800">
      {state}
    </span>
  );
}

